import { useEffect, useState } from "react";
import { api, SECTION_LABELS, formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Radio, CalendarDays, MapPin } from "lucide-react";
import LiveMeeting from "./LiveMeeting";

export default function LiveSection({ user, isAdmin }) {
  const [meetings, setMeetings] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [tick, setTick] = useState(0);

  const load = () => api.get("/meetings").then(r => setMeetings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[#EF3E36] font-bold flex items-center gap-1" data-testid="live-section-title">
          <Radio className="w-3.5 h-3.5" /> Assemblee
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2B3A33] mt-1">Riunione Live</h2>
        <p className="text-sm text-[#6B6558] mt-1">
          {isAdmin
            ? "Avvia la riunione in diretta, segna i presenti, apri votazioni e gestisci gli interventi."
            : "Quando una riunione è in corso potrai entrare, votare e chiedere la parola."}
        </p>
      </div>

      <div className="grid gap-3" data-testid="live-meetings-list">
        {meetings.length === 0 && (
          <div className="paper p-8 rounded-2xl text-center text-[#6B6558]">
            <CalendarDays className="w-8 h-8 mx-auto text-[#8A8272] mb-2" />
            Nessuna riunione in programma.
          </div>
        )}
        {meetings.map(m => (
          <LiveRow key={`${m.id}-${tick}`} m={m} isAdmin={isAdmin} onOpen={id => setOpenId(id)} />
        ))}
      </div>

      {openId && (
        <LiveMeeting meetingId={openId} onClose={() => { setOpenId(null); setTick(t => t + 1); }} />
      )}
    </div>
  );
}

function LiveRow({ m, isAdmin, onOpen }) {
  const [status, setStatus] = useState(null);
  const loadLive = () => api.get(`/meetings/${m.id}/live`).then(r => setStatus(r.data.status)).catch(() => setStatus(null));
  useEffect(() => { loadLive(); /* eslint-disable-next-line */ }, [m.id]);

  const start = async () => {
    try { await api.post(`/meetings/${m.id}/live/start`); onOpen(m.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore avvio riunione"); }
  };

  const isLive = status === "live";
  return (
    <div data-testid={`live-row-${m.id}`} className={`paper rounded-2xl p-5 flex items-center gap-4 ${isLive ? "ring-2 ring-[#EF3E36]" : ""}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isLive ? "bg-[#FDE8E7]" : "bg-[#EDEAE3]"}`}>
        <Radio className={`w-5 h-5 ${isLive ? "text-[#EF3E36]" : "text-[#8A8272]"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="font-display font-bold text-[#2B3A33] truncate">{m.title}</h4>
          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded-full role-${m.section}`}>{SECTION_LABELS[m.section] || m.section}</span>
          {isLive && <span className="text-[10px] uppercase tracking-widest font-bold text-[#EF3E36] inline-flex items-center gap-1"><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EF3E36] opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-[#EF3E36]"></span></span>In corso</span>}
        </div>
        <div className="text-xs text-[#8A8272] mt-0.5 flex flex-wrap gap-x-3">
          <span>{formatDateTime(m.start)}</span>
          {m.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.location}</span>}
        </div>
      </div>
      <div className="flex-shrink-0">
        {isLive ? (
          <button data-testid={`live-enter-${m.id}`} onClick={() => onOpen(m.id)} className="px-4 py-2 rounded-lg bg-[#EF3E36] text-white text-sm font-semibold hover:bg-[#C9302A] inline-flex items-center gap-1">
            <Radio className="w-4 h-4" /> Entra
          </button>
        ) : isAdmin ? (
          <button data-testid={`live-start-${m.id}`} onClick={start} className="px-4 py-2 rounded-lg bg-[#2B3A33] text-white text-sm font-semibold hover:opacity-90 inline-flex items-center gap-1">
            <Radio className="w-4 h-4" /> Avvia Live
          </button>
        ) : (
          <span className="text-xs text-[#8A8272]">Non in corso</span>
        )}
      </div>
    </div>
  );
}
